import {Content, LoadingButton, useBreakPoints, useTranslation} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import {ArrowForward} from '@mui/icons-material'
import {Box, Grid, Paper, Typography} from '@mui/material'
import React from 'react'
import {useFormContext} from 'react-hook-form'
import {useHistory} from 'react-router'

import {OrderChangeConfig, OrderChangeConfigOptions} from '../types/OrderChangeConfig'

import {ExtraDistance} from './OrderCancellation/ExtraDistance'
import {ChangeableFieldsSettings} from './OrderChange/ChangeableFieldsSettings'
import {OrderManagementConfigurationConstraints} from './OrderManagementConfigurationConstraints'
import {useStyles} from './OrderManagementConfigurationDetails.styles'

type Props = {
  mode: 'edit' | 'create' | 'copy'
  configId?: string
  onSaveButtonClick: () => void
  configOptions: OrderChangeConfigOptions
  isSubmitting: boolean
}

export const OrderManagementConfigurationDetails = ({
  mode,
  onSaveButtonClick,
  configOptions,
  isSubmitting
}: Props) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const {classes} = useStyles({isMobile})
  const history = useHistory()
  const {watch} = useFormContext<OrderChangeConfig>()
  const config = watch()
  const isFormReady = Object.keys(config).length > 0

  const formatTitle = () => {
    switch (mode) {
      case 'edit':
        return t('configurations.hconnect.orderManagement.configurationDetails.titleEdit')
      case 'create':
        return t('configurations.hconnect.orderManagement.configurationDetails.titleCreate')
      case 'copy':
        return t('configurations.hconnect.orderManagement.configurationDetails.titleCopy')
    }
  }

  if (!configOptions || !isFormReady) {
    return <div>{t('configurations.hconnect.orderManagement.configurationDetails.loading')}</div>
  }

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy={`order-change-configuration-title-${mode}`}
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          backToPageLabel={t('configurations.hconnect.orderManagement.configurationDetails.goBack')}
          backToPageUrlOnClick={() => history.goBack()}
        >
          {formatTitle()}
        </PageHeader>
      </div>
      <Grid container className={classes.gridContainer} spacing={2}>
        <Grid item lg={3} md={3} sm={12} xs={12} className={classes.gridItem}>
          <Paper elevation={4} className={classes.paper}>
            <Typography className={classes.cardTitle}>
              {t('configurations.hconnect.orderManagement.configurationDetails.constraints.title')}
            </Typography>
            <OrderManagementConfigurationConstraints disabled={mode === 'edit'} />
          </Paper>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12} className={classes.gridItem}>
          <Paper elevation={4} className={classes.paper}>
            <OrderCancellationSettings />
            <OrderChangeSettings configOptions={configOptions} />
            <Box className={classes.saveButtonBox}>
              <LoadingButton
                startIcon={<ArrowForward fontSize="small" />}
                variant="outlined"
                color="primary"
                style={{background: 'rgb(1, 106, 212', textTransform: 'none'}}
                onClick={onSaveButtonClick}
                loading={isSubmitting}
                data-test-id="order-change-configuration-save-button"
              >
                {mode === 'edit'
                  ? t('configurations.hconnect.orderManagement.configurationDetails.buttonSave')
                  : t(
                      `configurations.hconnect.orderManagement.configurationDetails.button${
                        mode.charAt(0).toUpperCase() + mode.slice(1)
                      }`
                    )}
              </LoadingButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Content>
  )
}

const OrderChangeSettings = ({configOptions}: {configOptions: OrderChangeConfigOptions}) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const {classes} = useStyles({isMobile})
  const {watch} = useFormContext<OrderChangeConfig>()
  const businessLine = watch('businessLine')
  return (
    <>
      <Typography className={classes.cardTitle}>
        {t('configurations.hconnect.orderManagement.configurationDetails.orderChange.title')}
      </Typography>
      {businessLine === 'RMC' && <ChangeableFieldsSettings configOptions={configOptions} />}
    </>
  )
}

const OrderCancellationSettings = () => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const {classes} = useStyles({isMobile})
  return (
    <>
      <Typography className={classes.cardTitle}>
        {t('configurations.hconnect.orderManagement.configurationDetails.orderCancellation.title')}
      </Typography>
      <ExtraDistance />
    </>
  )
}
